import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { ChevronLeftIcon, AlertCircleIcon, InfoIcon, CheckCircle2Icon, CheckCheckIcon, XIcon } from 'lucide-react';
interface NotificationsProps {
  onBack: () => void;
}
export function Notifications({
  onBack
}: NotificationsProps) {
  const {
    colors
  } = useTheme();
  const [notifications, setNotifications] = useState([{
    id: '1',
    type: 'critical',
    title: 'Critical Alert',
    message: 'Hand hygiene compliance critical in Surgical Ward',
    time: '10 mins ago',
    timestamp: new Date(Date.now() - 10 * 60 * 1000),
    read: false
  }, {
    id: '2',
    type: 'warning',
    title: 'Pending Checklist',
    message: 'Pre-procedure checklist pending review',
    time: '25 mins ago',
    timestamp: new Date(Date.now() - 25 * 60 * 1000),
    read: false
  }, {
    id: '3',
    type: 'success',
    title: 'Checklist Completed',
    message: 'Hand hygiene checklist completed successfully',
    time: '1 hour ago',
    timestamp: new Date(Date.now() - 60 * 60 * 1000),
    read: true
  }, {
    id: '4',
    type: 'info',
    title: 'System Update',
    message: 'New features available in the latest update',
    time: '2 hours ago',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    read: true
  }, {
    id: '5',
    type: 'warning',
    title: 'Low Compliance Alert',
    message: 'Ward compliance below 85% threshold',
    time: 'Yesterday',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
    read: true
  }]);
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(notif => ({
      ...notif,
      read: true
    })));
  };
  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(notif => notif.id === id ? {
      ...notif,
      read: true
    } : notif));
  };
  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'critical':
        return AlertCircleIcon;
      case 'warning':
        return AlertCircleIcon;
      case 'success':
        return CheckCircle2Icon;
      case 'info':
        return InfoIcon;
      default:
        return InfoIcon;
    }
  };
  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'critical':
        return colors.error;
      case 'warning':
        return colors.warning;
      case 'success':
        return colors.success;
      case 'info':
        return colors.accent;
      default:
        return colors.textSecondary;
    }
  };
  const groupNotifications = () => {
    const today: typeof notifications = [];
    const earlier: typeof notifications = [];
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    notifications.forEach(notif => {
      if (notif.timestamp >= todayStart) {
        today.push(notif);
      } else {
        earlier.push(notif);
      }
    });
    return {
      today,
      earlier
    };
  };
  const {
    today,
    earlier
  } = groupNotifications();
  const unreadCount = notifications.filter(n => !n.read).length;
  return <div className="min-h-screen -m-4" style={{
    backgroundColor: colors.background
  }} data-id="element-267">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b" style={{
      backgroundColor: colors.cardSurface,
      borderColor: colors.divider
    }} data-id="element-268">
        <div className="flex items-center justify-between p-4" data-id="element-269">
          <div className="flex items-center gap-4" data-id="element-270">
            <button onClick={onBack} className="p-2 -ml-2 rounded-lg transition-colors hover:bg-opacity-10" style={{
            color: colors.textPrimary
          }} data-id="element-271">
              <ChevronLeftIcon size={24} data-id="element-272" />
            </button>
            <div data-id="element-273">
              <h1 className="text-xl font-bold" style={{
              color: colors.textPrimary
            }} data-id="element-274">
                Notifications
              </h1>
              {unreadCount > 0 && <p className="text-sm" style={{
              color: colors.textSecondary
            }} data-id="element-275">
                  {unreadCount} unread
                </p>}
            </div>
          </div>
          {unreadCount > 0 && <button onClick={markAllAsRead} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors" style={{
          color: colors.accent,
          backgroundColor: colors.accent + '15'
        }} data-id="element-276">
              <CheckCheckIcon size={16} data-id="element-277" />
              Mark all read
            </button>}
        </div>
      </div>
      <div className="p-4 space-y-6" data-id="element-278">
        {/* Today Section */}
        {today.length > 0 && <div data-id="element-279">
            <h2 className="text-sm font-bold uppercase tracking-wider mb-3 px-2" style={{
          color: colors.textSecondary
        }} data-id="element-280">
              Today
            </h2>
            <div className="space-y-2" data-id="element-281">
              {today.map(notification => {
            const Icon = getNotificationIcon(notification.type);
            const color = getNotificationColor(notification.type);
            return <div key={notification.id} className="rounded-xl overflow-hidden transition-all hover:scale-[1.01]" style={{
              backgroundColor: notification.read ? colors.cardSurface : color + '08',
              border: `1px solid ${notification.read ? colors.divider : color + '30'}`
            }} data-id="element-282">
                    <div className="flex items-start gap-4 p-4" data-id="element-283">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
                  backgroundColor: color + '20'
                }} data-id="element-284">
                        <Icon size={24} style={{
                    color
                  }} data-id="element-285" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-286">
                        <div className="flex items-start justify-between gap-2 mb-1" data-id="element-287">
                          <h3 className="font-semibold" style={{
                      color: colors.textPrimary
                    }} data-id="element-288">
                            {notification.title}
                          </h3>
                          {!notification.read && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5" style={{
                      backgroundColor: color
                    }} data-id="element-289" />}
                        </div>
                        <p className="text-sm mb-2 leading-relaxed" style={{
                    color: colors.textSecondary
                  }} data-id="element-290">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between" data-id="element-291">
                          <span className="text-xs" style={{
                      color: colors.textSecondary
                    }} data-id="element-292">
                            {notification.time}
                          </span>
                          <div className="flex items-center gap-2" data-id="element-293">
                            {!notification.read && <button onClick={() => markAsRead(notification.id)} className="text-xs px-3 py-1 rounded-full transition-colors" style={{
                        color: colors.accent,
                        backgroundColor: colors.accent + '15'
                      }} data-id="element-294">
                                Mark read
                              </button>}
                            <button onClick={() => deleteNotification(notification.id)} className="p-1 rounded-full transition-colors hover:bg-opacity-10" style={{
                        color: colors.textSecondary
                      }} data-id="element-295">
                              <XIcon size={16} data-id="element-296" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>;
          })}
            </div>
          </div>}
        {/* Earlier Section */}
        {earlier.length > 0 && <div data-id="element-297">
            <h2 className="text-sm font-bold uppercase tracking-wider mb-3 px-2" style={{
          color: colors.textSecondary
        }} data-id="element-298">
              Earlier
            </h2>
            <div className="space-y-2" data-id="element-299">
              {earlier.map(notification => {
            const Icon = getNotificationIcon(notification.type);
            const color = getNotificationColor(notification.type);
            return <div key={notification.id} className="rounded-xl overflow-hidden transition-all hover:scale-[1.01]" style={{
              backgroundColor: colors.cardSurface,
              border: `1px solid ${colors.divider}`,
              opacity: 0.9
            }} data-id="element-300">
                    <div className="flex items-start gap-4 p-4" data-id="element-301">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
                  backgroundColor: color + '15'
                }} data-id="element-302">
                        <Icon size={24} style={{
                    color,
                    opacity: 0.7
                  }} data-id="element-303" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-304">
                        <h3 className="font-semibold mb-1" style={{
                    color: colors.textPrimary
                  }} data-id="element-305">
                          {notification.title}
                        </h3>
                        <p className="text-sm mb-2 leading-relaxed" style={{
                    color: colors.textSecondary
                  }} data-id="element-306">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between" data-id="element-307">
                          <span className="text-xs" style={{
                      color: colors.textSecondary
                    }} data-id="element-308">
                            {notification.time}
                          </span>
                          <button onClick={() => deleteNotification(notification.id)} className="p-1 rounded-full transition-colors hover:bg-opacity-10" style={{
                      color: colors.textSecondary
                    }} data-id="element-309">
                            <XIcon size={16} data-id="element-310" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>;
          })}
            </div>
          </div>}
        {/* Empty State */}
        {notifications.length === 0 && <div className="text-center py-16" data-id="element-311">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{
          backgroundColor: colors.divider
        }} data-id="element-312">
              <CheckCircle2Icon size={40} style={{
            color: colors.textSecondary
          }} data-id="element-313" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{
          color: colors.textPrimary
        }} data-id="element-314">
              All caught up!
            </h3>
            <p style={{
          color: colors.textSecondary
        }} data-id="element-315">
              You have no notifications
            </p>
          </div>}
      </div>
    </div>;
}